/**
 * WO#4a — Canonical zone geometry bridge (frontend types + fetcher).
 *
 * The frontend consumes `/api/v1/sale/zones` as an OPAQUE canonical
 * artifact: resolved disjoint rectangles + identity. It NEVER classifies
 * zones, NEVER computes precedence, NEVER derives sellability/price from
 * this payload. `sale-api.quote` remains the sole financial authority.
 */
import { API_BASE, ApiError } from "./api";
import type { ErrorEnvelope } from "./sale-types";

// Canonical resolved zone role labels — match backend `ZoneRoleLiteral`.
export type ZoneRole =
  | "STANDARD"
  | "PREMIUM"
  | "VIP_CORE_SELLABLE"
  | "LOCKED_GRID_ZERO"
  | "LOCKED_RESERVE";

export interface ZoneRegion {
  zone: ZoneRole;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ZoneManifestIdentity {
  chain_id: number;
  sale_contract_address: string;
  runtime_template_hash: string;
  manifest_hash: string;
  geometry_source_hash: string;
}

export interface ZoneManifest {
  schema_version: "1.0.0";
  grid: { width: number; height: number };
  identity: ZoneManifestIdentity;
  regions: ZoneRegion[];
}

/** Discriminated union representing the fetch state of the manifest. */
export type ZoneManifestState =
  | { status: "loading" }
  | { status: "ready"; manifest: ZoneManifest }
  | { status: "unavailable"; code: string; message: string };

async function apiGet<T>(path: string): Promise<T> {
  const url = `${API_BASE}${path}`;
  const resp = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!resp.ok) {
    const b = (body as Partial<ErrorEnvelope>) || {};
    throw new ApiError(
      resp.status,
      b.code || "UNKNOWN",
      b.message || resp.statusText,
      b.details,
    );
  }
  return body as T;
}

// Wire-format validator — kept minimal but strict. Any deviation → throw
// so the caller surfaces `unavailable`, never a partial manifest.
function assertManifest(raw: unknown): asserts raw is ZoneManifest {
  if (!raw || typeof raw !== "object") {
    throw new Error("manifest_not_object");
  }
  const m = raw as Record<string, unknown>;
  if (m.schema_version !== "1.0.0") throw new Error("schema_version_mismatch");
  const grid = m.grid as { width?: number; height?: number } | undefined;
  if (!grid || grid.width !== 1000 || grid.height !== 1000) {
    throw new Error("grid_shape_invalid");
  }
  const id = m.identity as Record<string, unknown> | undefined;
  if (!id
    || typeof id.chain_id !== "number"
    || typeof id.sale_contract_address !== "string"
    || typeof id.runtime_template_hash !== "string"
    || typeof id.manifest_hash !== "string"
    || typeof id.geometry_source_hash !== "string") {
    throw new Error("identity_incomplete");
  }
  const regions = m.regions;
  if (!Array.isArray(regions) || regions.length === 0) {
    throw new Error("regions_missing");
  }
  for (const r of regions) {
    const rr = r as Record<string, unknown>;
    if (typeof rr.zone !== "string"
      || typeof rr.x !== "number" || typeof rr.y !== "number"
      || typeof rr.w !== "number" || typeof rr.h !== "number") {
      throw new Error("region_shape_invalid");
    }
  }
}

/** Fetch `/api/v1/sale/zones`. Callers are responsible for identity
 * cross-check against `SaleAuthorityProvider.currentAuthority`.
 */
export async function fetchZoneManifest(): Promise<ZoneManifest> {
  const raw = await apiGet<unknown>("/api/v1/sale/zones");
  assertManifest(raw);
  return raw;
}

/** Convenience — normalise an address for equality compare. */
export function normAddress(a: string | null | undefined): string {
  return (a ?? "").toLowerCase();
}

/** True iff two identities describe the same deployed Sale + template
 * + content. Used by both cache invalidation and the render guard.
 */
export function identityMatches(
  a: Pick<ZoneManifestIdentity, "chain_id" | "sale_contract_address" | "runtime_template_hash">,
  b: Pick<ZoneManifestIdentity, "chain_id" | "sale_contract_address" | "runtime_template_hash">,
): boolean {
  return a.chain_id === b.chain_id
    && normAddress(a.sale_contract_address) === normAddress(b.sale_contract_address)
    && a.runtime_template_hash.toLowerCase() === b.runtime_template_hash.toLowerCase();
}
